import { type RefObject, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useAddPrintoutPreset, useRemovePrintoutPreset, useRenamePrintoutPreset, useSelectPrintoutPreset } from "../../hooks/usePrintoutPresets";
import type { PresetAutosave } from "./detailedPresetState";
import { nextPresetName, type PresetSettings, type PrintoutPreset } from "./printoutPresets";
import type { LivePresetState } from "./useLivePreset";

type Params = Pick<LivePresetState, "liveRef" | "replaceLive" | "sessionEditsRef"> & {
	autosave: PresetAutosave;
	/** The saved printouts as of right now. */
	presetsRef: RefObject<PrintoutPreset[]>;
};

/**
 * What the user does to the saved printouts: edit the one on screen, switch, add, remove, rename. Every
 * action that leaves the printout on screen first sends its waiting edit, to the printout it was made on.
 */
export const usePresetActions = ({ liveRef, replaceLive, sessionEditsRef, autosave, presetsRef }: Params) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { mutate: selectOnServer } = useSelectPrintoutPreset();
	const { mutate: addOnServer, isPending: isAdding } = useAddPrintoutPreset();
	const { mutateAsync: removeOnServer } = useRemovePrintoutPreset();
	const { mutateAsync: renameOnServer } = useRenamePrintoutPreset();

	/** Replaces a saved printout's on-screen settings and schedules its save. False if it isn't the one on screen. */
	const updatePresetSettings = useCallback(
		(presetId: string, settings: PresetSettings): boolean => {
			const preset = presetsRef.current.find((candidate) => candidate.id === presetId);
			if (!preset || liveRef.current?.presetId !== presetId) return false;
			replaceLive({ presetId, settings });
			sessionEditsRef.current.set(presetId, settings);
			autosave.schedule({ departmentId, presetId, presetName: preset.name, settings });
			return true;
		},
		[autosave, departmentId, liveRef, presetsRef, replaceLive, sessionEditsRef],
	);

	const selectPreset = useCallback(
		(presetId: string | null) => {
			if ((liveRef.current ? liveRef.current.presetId : null) === presetId) return;
			void autosave.flush();
			selectOnServer(presetId);
		},
		[autosave, liveRef, selectOnServer],
	);

	/** Adds a printout starting from `settings`; the server makes it the one in use. */
	const addPreset = useCallback(
		(settings: PresetSettings) => {
			void autosave.flush();
			addOnServer({
				name: nextPresetName(
					presetsRef.current.map((preset) => preset.name),
					t,
				),
				settings,
			});
		},
		[autosave, addOnServer, presetsRef, t],
	);

	/** Resolves whether the printout was removed (a failure is already shown by the mutation). */
	const removePreset = useCallback(
		async (presetId: string): Promise<boolean> => {
			// Saved first, not dropped: if the removal fails, the printout is still there with its edits.
			await autosave.flush();
			try {
				await removeOnServer(presetId);
			} catch {
				return false; // Shown to the user and tracked by `useRemovePrintoutPreset`.
			}
			sessionEditsRef.current.delete(presetId);
			return true;
		},
		[autosave, removeOnServer, sessionEditsRef],
	);

	/** Resolves whether the rename was saved (a failure is already shown by the mutation). */
	const renamePreset = useCallback(
		async (presetId: string, name: string): Promise<boolean> => {
			try {
				await renameOnServer({ presetId, name });
				return true;
			} catch {
				return false; // Shown to the user and tracked by `useRenamePrintoutPreset`.
			}
		},
		[renameOnServer],
	);

	return { updatePresetSettings, selectPreset, addPreset, removePreset, renamePreset, isAdding };
};
